import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox, CheckboxGroup } from '../../../components/ui/Checkbox';

const UserForm = ({ user, onSave, onCancel, isOpen }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    status: 'active',
    permissions: [],
    department: '',
    phone: '',
    location: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const roleOptions = [
    { value: 'owner', label: 'Owner/Executive' },
    { value: 'chartering', label: 'Chartering Desk' },
    { value: 'finance', label: 'Finance Team' },
    { value: 'compliance', label: 'Compliance Officer' },
    { value: 'operations', label: 'Operations Manager' },
    { value: 'technical', label: 'Technical Superintendent' },
    { value: 'admin', label: 'System Administrator' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending Activation' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const departmentOptions = [
    { value: 'operations', label: 'Operations' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'technical', label: 'Technical' },
    { value: 'finance', label: 'Finance' },
    { value: 'legal', label: 'Legal & Compliance' },
    { value: 'it', label: 'Information Technology' }
  ];

  const permissionGroups = {
    'Fleet Management': [
      { id: 'fleet_view', label: 'View Fleet Dashboard' },
      { id: 'fleet_edit', label: 'Edit Fleet Settings' },
      { id: 'vessel_manage', label: 'Manage Vessels' }
    ],
    'Voyage Operations': [
      { id: 'voyage_plan', label: 'Create Voyage Plans' },
      { id: 'voyage_edit', label: 'Edit Voyage Plans' },
      { id: 'route_optimize', label: 'Route Optimization' }
    ],
    'Emissions & Compliance': [
      { id: 'emissions_calculate', label: 'Calculate Emissions' },
      { id: 'compliance_reports', label: 'Generate Compliance Reports' },
      { id: 'audit_trail', label: 'View Audit Trails' }
    ],
    'EUA Trading': [
      { id: 'eua_view', label: 'View EUA Positions' },
      { id: 'eua_trade', label: 'Execute EUA Trades' },
      { id: 'eua_forecast', label: 'Access EUA Forecasts' }
    ],
    'User Management': [
      { id: 'user_view', label: 'View Users' },
      { id: 'user_create', label: 'Create Users' },
      { id: 'user_edit', label: 'Edit Users' },
      { id: 'user_delete', label: 'Delete Users' }
    ],
    'System Administration': [
      { id: 'system_settings', label: 'System Settings' },
      { id: 'api_access', label: 'API Access' },
      { id: 'data_export', label: 'Data Export' }
    ]
  };

  useEffect(() => {
    if (user) {
      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        role: user?.role || '',
        status: user?.status || 'active',
        permissions: user?.permissions || [],
        department: user?.department || '',
        phone: user?.phone || '',
        location: user?.location || ''
      });
    } else {
      setFormData({
        name: '',
        email: '',
        role: '',
        status: 'active',
        permissions: [],
        department: '',
        phone: '',
        location: ''
      });
    }
    setErrors({});
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePermissionChange = (permissionId, checked) => {
    setFormData(prev => ({
      ...prev,
      permissions: checked
        ? [...prev?.permissions, permissionId]
        : prev?.permissions?.filter(p => p !== permissionId)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.role) {
      newErrors.role = 'Role is required';
    }

    if (formData?.permissions?.length === 0) {
      newErrors.permissions = 'At least one permission must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getRolePermissions = (role) => {
    const rolePermissionMap = {
      owner: Object.values(permissionGroups)?.flat()?.map(p => p?.id),
      admin: Object.values(permissionGroups)?.flat()?.map(p => p?.id),
      chartering: ['fleet_view', 'voyage_plan', 'voyage_edit', 'route_optimize', 'emissions_calculate'],
      finance: ['fleet_view', 'eua_view', 'eua_trade', 'eua_forecast', 'compliance_reports'],
      compliance: ['fleet_view', 'emissions_calculate', 'compliance_reports', 'audit_trail'],
      operations: ['fleet_view', 'voyage_plan', 'vessel_manage', 'emissions_calculate'],
      technical: ['fleet_view', 'vessel_manage', 'emissions_calculate', 'compliance_reports']
    };
    return rolePermissionMap?.[role] || [];
  };

  const handleRoleChange = (role) => {
    handleInputChange('role', role);
    const rolePermissions = getRolePermissions(role);
    handleInputChange('permissions', rolePermissions);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-1200 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" onClick={onCancel} />
        
        <div className="relative bg-card rounded-lg border border-border shadow-maritime-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground">
              {user ? 'Edit User' : 'Add New User'}
            </h2>
            <Button variant="ghost" size="sm" onClick={onCancel}>
              <Icon name="X" size={20} strokeWidth={2} />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-foreground">Basic Information</h3>
                  
                  <Input
                    label="Full Name"
                    type="text"
                    required
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    error={errors?.name}
                    placeholder="Enter full name"
                  />

                  <Input
                    label="Email Address"
                    type="email"
                    required
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    error={errors?.email}
                    placeholder="user@company.com"
                  />

                  <Input
                    label="Phone Number"
                    type="tel"
                    value={formData?.phone}
                    onChange={(e) => handleInputChange('phone', e?.target?.value)}
                    placeholder="+1 (555) 123-4567"
                  />

                  <Input
                    label="Location"
                    type="text"
                    value={formData?.location}
                    onChange={(e) => handleInputChange('location', e?.target?.value)}
                    placeholder="City, Country"
                  />
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-foreground">Role & Access</h3>
                  
                  <Select
                    label="Role"
                    required
                    options={roleOptions}
                    value={formData?.role}
                    onChange={handleRoleChange}
                    error={errors?.role}
                    placeholder="Select user role"
                  />

                  <Select
                    label="Department"
                    options={departmentOptions}
                    value={formData?.department}
                    onChange={(value) => handleInputChange('department', value)}
                    placeholder="Select department"
                  />

                  <Select
                    label="Status"
                    options={statusOptions}
                    value={formData?.status}
                    onChange={(value) => handleInputChange('status', value)}
                  />
                </div>
              </div>

              {/* Permissions */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-foreground">Permissions</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleInputChange('permissions', [])}
                  >
                    Clear All
                  </Button>
                </div>
                
                {errors?.permissions && (
                  <p className="text-sm text-error">{errors?.permissions}</p>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {Object.entries(permissionGroups)?.map(([groupName, permissions]) => (
                    <div key={groupName} className="space-y-3">
                      <h4 className="text-sm font-medium text-foreground border-b border-border pb-2">
                        {groupName}
                      </h4>
                      <CheckboxGroup>
                        {permissions?.map((permission) => (
                          <Checkbox
                            key={permission?.id}
                            label={permission?.label}
                            checked={formData?.permissions?.includes(permission?.id)}
                            onChange={(e) => handlePermissionChange(permission?.id, e?.target?.checked)}
                          />
                        ))}
                      </CheckboxGroup>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/30">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" loading={isLoading}>
                {user ? 'Update User' : 'Create User'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserForm;